import React, { useMemo, useState } from 'react';
import { Textarea } from '@televet/kibble-ui/build/components/Textarea';
import { Divider, Flex, VStack } from '@televet/kibble-ui/build/chakra';

import { useAppSelector } from 'state/hooks';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useClipboard } from 'shared/hooks/useClipboard';
import useHotJar from 'shared/hooks/useHotjar';

import useClinicUser from 'shared/hooks/useClinicUser';

import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { useTracking } from 'shared/providers/TrackingProvider';
import { AiRecapEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useGetAppointmentModalRecapsQuery } from 'shared/types/graphql';
import format from 'date-fns/format';

export const AiRecapTranscription = (): JSX.Element => {
  const { trackEvent } = useTracking();
  const { trackEvent: trackHotJarEvent } = useHotJar();
  const { copy } = useClipboard();

  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const { appointmentId } = useAppSelector((state) => state.appointmentsModal);
  const { aiRecapWriteback } = useAppSelector((state) => state.appointmentsModal.appointmentDraft);
  const { currentClinic } = useClinicUser();
  const { isExportToPimsSupported, primaryIntegrationName } = useIntegrationsProvider();
  const isAiRecapWritebackEnabled = isExportToPimsSupported && currentClinic?.clinicSetting?.aiRecapWritebackEnabled;

  const { data, loading } = useGetAppointmentModalRecapsQuery({
    variables: {
      where: {
        appointmentId: { equals: appointmentId ?? '' },
      },
    },
    skip: !appointmentId,
  });

  const transcriptions = useMemo(() => {
    if (loading || !data?.findManyRecap) return '';

    return data.findManyRecap
      .map(
        (recap) => `${format(new Date(recap.createdAt), "EEEE, MMM d, yyyy 'at' h:mm a")}\n ${recap.transcriptionText}`,
      )
      .join('\n------------------\n');
  }, [data, loading]);

  return (
    <VStack className="AppointmentModal__Notes" w="100%" align="flex-start" position="relative">
      <>
        <Textarea
          mt={4}
          value={transcriptions}
          labelStyle={{ fontWeight: 'bold' }}
          data-testid="ai-recap-transcription-textarea"
          cursor="default"
          isReadOnly
        />

        <Flex justifyContent="space-between" w="100%">
          <Flex alignItems="center">
            <Text as="p" size="sm" variant="subtle" mr={1}>
              {feedbackSubmitted ? 'Thanks for your feedback!' : 'How was this AI Recap?'}
            </Text>
            {!feedbackSubmitted && (
              <>
                <Button
                  iconName="thumbsUp"
                  size="sm"
                  variant="ghostNeutral"
                  onClick={(): void => {
                    trackHotJarEvent('AI Recap Positive Feedback');
                    trackEvent(AiRecapEvents.PositiveFeedback, { appointmentId });
                    setFeedbackSubmitted(true);
                  }}
                />
                <Button
                  iconName="thumbsDown"
                  size="sm"
                  variant="ghostNeutral"
                  onClick={(): void => {
                    trackHotJarEvent('AI Recap Negative Feedback');
                    trackEvent(AiRecapEvents.NegativeFeedback, { appointmentId });
                    setFeedbackSubmitted(true);
                  }}
                />
              </>
            )}
          </Flex>
          {!!transcriptions?.length && (
            <Button
              onClick={(): void => copy(transcriptions)}
              variant="secondarySubtle"
              iconName="twoSquares"
              size="sm"
            >
              Copy Transcription
            </Button>
          )}
        </Flex>
        {isAiRecapWritebackEnabled && !aiRecapWriteback && (
          <Alert
            status="info"
            description={`AI Recap Notes and any edits made to it will sync to ${primaryIntegrationName} nightly.`}
            hideCloseButton
          />
        )}
        <Divider />
      </>
    </VStack>
  );
};
